"use client";

/**
 * Identifies the user against both PostHog projects:
 *   - Marketing (own posthog-js instance, identified by email).
 *   - Product (singleton from <AnalyticsProvider>, identified by user.id +
 *     `$groups.organization`). Without the product identify, browser events
 *     fire anonymously while server events use user.id, splitting one
 *     human into two PostHog people.
 *
 * Mount once inside the workspace layout, below UserProvider/OrgProvider.
 */
import { useSupabase } from "@/app/supabase-provider";
import { clientEnv } from "@/clientEnv";
import { useSessionWorkspace } from "@/components/providers/org_provider";
import { useUser } from "@/components/providers/user_provider";
import { initPosthog } from "@/utils/analytics-provider";
import { SHARED_PRIVACY_CONFIG } from "@/utils/posthog/browserConfig";
import {
  getPostHogUiHost,
  POSTHOG_PROXY_PREFIX,
} from "@/utils/posthog/regions";
import { resolveIsAdminViewer } from "@/utils/productAnalytics/baseProperties";
import { productAnalytics } from "@/utils/productAnalytics/client";
import { PostHog } from "posthog-js";
import { useEffect, useRef, useState } from "react";

export function AnalyticsIdentify() {
  const { user: authUser } = useSupabase();
  const { user } = useUser();
  const { org: workspace } = useSessionWorkspace();

  // === Marketing PostHog (separate project) ===
  const lastIdentifiedMarketingEmail = useRef<string | null>(null);
  const [marketingPosthog, setMarketingPosthog] = useState<PostHog | null>(
    null
  );

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      clientEnv.NEXT_PUBLIC_POSTHOG_MARKETING_CAPTURE_KEY &&
      !marketingPosthog
    ) {
      const posthog = new PostHog();
      const isProductionOrPreview =
        clientEnv.NEXT_PUBLIC_VERCEL_ENV === "production" ||
        clientEnv.NEXT_PUBLIC_VERCEL_ENV === "preview";
      posthog.init(clientEnv.NEXT_PUBLIC_POSTHOG_MARKETING_CAPTURE_KEY, {
        api_host: POSTHOG_PROXY_PREFIX,
        ui_host: getPostHogUiHost(),
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        // Both instances share one web-vitals loader; keep this one from loading the build without attribution.
        capture_performance: { web_vitals: false },
        ...SHARED_PRIVACY_CONFIG,
        disable_session_recording: !isProductionOrPreview,
        persistence: "localStorage+cookie",
        person_profiles: "always",
        cross_subdomain_cookie: true,
      });
      setMarketingPosthog(posthog);
    }
  }, [marketingPosthog]);

  useEffect(() => {
    if (!marketingPosthog || !authUser?.email) return;
    if (lastIdentifiedMarketingEmail.current === authUser.email) return;

    const email = authUser.email.toLowerCase();
    lastIdentifiedMarketingEmail.current = authUser.email;

    marketingPosthog.identify(email, {
      email,
      user_id: authUser.id,
      $email: email,
    });
  }, [marketingPosthog, authUser?.email, authUser?.id]);

  // === Product PostHog (the singleton init'd by AnalyticsProvider) ===
  const userId = user?.id;
  const userEmail = user?.email;
  const userName = user?.name;
  const isSuperAdmin = user?.isSuperAdmin ?? false;
  useEffect(() => {
    if (!userId) return;
    // This child effect runs before AnalyticsProvider's init, and posthog-js drops pre-init calls.
    initPosthog();
    productAnalytics.identifyUser({
      userId,
      email: userEmail,
      name: userName,
      isAdminViewer: resolveIsAdminViewer({
        email: userEmail,
        isSuperAdmin,
      }),
    });
  }, [userId, userEmail, userName, isSuperAdmin]);

  const workspaceId = workspace?.id;
  const workspaceName = workspace?.name;
  useEffect(() => {
    if (!workspaceId) return;
    initPosthog();
    productAnalytics.identifyOrganization({
      orgId: workspaceId,
      name: workspaceName,
    });
  }, [workspaceId, workspaceName]);

  return null;
}
